import { useState, useEffect, useCallback } from 'react';
import { api } from '../../api';
import Modal from '../../components/Modal';
import AccountSelector from '../../components/AccountSelector';

// ============================================================
// EditRequestModal (Aug 4) — bucket-aware edit form for Sales
// submissions, opened from RequestDetailPage.
//
// Mirrors the server whitelist: PATCH /api/requests/:id for every
// bucket except sign_request / tagging_request, which go through
// PATCH /api/sign-requests/:id and /api/tagging-requests/:id so date and
// estimate changes revalidate capacity. Photos are untouched (v1). Only changed fields are sent.
// ============================================================

const WORK_TYPE_BUTTONS_BY_BUCKET = {
  striping_bid: [
    { key: 'striping',         label: 'Striping' },
    { key: 'pressure_washing', label: 'Pressure Washing' },
    { key: 'both',             label: 'Both' },
  ],
  valet_trash: [
    { key: 'valet', label: 'Valet Trash' },
    { key: 'bulk',  label: 'Bulk Trash' },
    { key: 'both',  label: 'Both' },
  ],
};

const labelStyle = { fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', display: 'block', marginBottom: 4 };
const req = <span style={{ color: '#ef4444' }}>*</span>;

function fmtDuration(mins) {
  if (!mins) return '';
  if (mins < 60) return `${mins} minutes`;
  const h = mins / 60;
  return h === 1 ? '1 hour' : `${h} hours`;
}

function fmtDateLong(dateStr) {
  const d = new Date(`${dateStr}T12:00:00Z`);
  return d.toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });
}

function shiftMonth(ym, delta) {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

export default function EditRequestModal({ request, onClose, onSaved }) {
  const type = request.type;
  // Sign Request and Tagging Request are the same editor — same fields, same
  // capacity-revalidating PATCH — only the column names, labels and endpoint
  // differ. FIELD_JOB picks the right set; isSign means "this editor".
  const isTagging = type === 'tagging_request';
  const isSign   = type === 'sign_request' || isTagging;
  const F = isTagging
    ? {
      regionCol: 'tagging_region', summaryCol: 'tagging_work_summary',
      specialCol: 'special_tagging_description', specialLabel: 'Special Tagging Instructions',
      specialError: 'Describe the special tagging work',
      specialPlaceholder: 'Anything the tagger needs to know before they go out?',
      manager: 'Tagging Manager',
      getConfig: api.getTaggingRequestConfig, getAvailability: api.getTaggingAvailability,
      update: api.updateTaggingRequest,
    }
    : {
      regionCol: 'sign_region', summaryCol: 'sign_work_summary',
      specialCol: 'special_sign_description', specialLabel: 'Custom / Special Sign Description',
      specialError: 'Describe the custom/special sign',
      specialPlaceholder: 'What does this sign need to say / look like?',
      manager: 'Sign Manager',
      getConfig: api.getSignRequestConfig, getAvailability: api.getSignAvailability,
      update: api.updateSignRequest,
    };
  const isItem   = type === 'item_request';
  const isEvent  = type === 'sponsored_event';
  const isVendor = Object.prototype.hasOwnProperty.call(WORK_TYPE_BUTTONS_BY_BUCKET, type);
  const towStyle = !isSign && !isItem && !isEvent && !isVendor;

  const [bucket, setBucket] = useState(null);       // request_types row (reasons, amount options)
  const [signConfig, setSignConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // ── shared field state, prefilled from the request ──
  const [property, setProperty] = useState(
    request.property_id ? { id: request.property_id, name: request.account_name || '' } : null
  );
  const [reasonId, setReasonId] = useState(request.reason_id || '');
  const [vehicleInfo, setVehicleInfo] = useState(request.vehicle_info || '');
  const [notes, setNotes] = useState(request.notes || '');
  const [refundCategory, setRefundCategory] = useState(request.refund_category || '');
  const [amount, setAmount] = useState(request.amount_requested != null ? String(request.amount_requested) : '');
  const [payerName, setPayerName] = useState(request.payer_name || '');
  const [payerPhone, setPayerPhone] = useState(request.payer_phone || '');
  const [payerEmail, setPayerEmail] = useState(request.payer_email || '');
  const [originalPaymentMethod, setOriginalPaymentMethod] = useState(request.original_payment_method || '');
  const [releaseDocInfo, setReleaseDocInfo] = useState(request.release_doc_info || '');
  const [checkMailingAddress, setCheckMailingAddress] = useState(request.check_mailing_address || '');
  const [itemCount, setItemCount] = useState(request.item_count != null ? String(request.item_count) : '');
  const [itemTypeOther, setItemTypeOther] = useState(request.item_type_other || '');
  const [requestedDate, setRequestedDate] = useState(request.requested_date || '');
  const [requestorName, setRequestorName] = useState(request.requestor_name || '');
  const [requestorEmail, setRequestorEmail] = useState(request.requestor_email || '');
  const [scopeOfWork, setScopeOfWork] = useState(request.scope_of_work || '');
  const [workRequestType, setWorkRequestType] = useState(request.work_request_type || '');
  const [requestedTimeline, setRequestedTimeline] = useState(request.requested_timeline || '');

  // ── sign-specific state ──
  const [workTypeIds, setWorkTypeIds] = useState([]);
  const [workTypesLoaded, setWorkTypesLoaded] = useState(false);
  const [originalWorkTypeIds, setOriginalWorkTypeIds] = useState([]);
  const [specialSignDescription, setSpecialSignDescription] = useState(request[isTagging ? 'special_tagging_description' : 'special_sign_description'] || '');
  const [workAddress, setWorkAddress] = useState(request.work_address || '');
  const [accessInstructions, setAccessInstructions] = useState(request.access_instructions || '');
  const [estimatedMinutes, setEstimatedMinutes] = useState(request.estimated_minutes || '');
  const [scheduledDate, setScheduledDate] = useState(request.scheduled_date || '');
  const [frequency, setFrequency] = useState(request.tagging_frequency || 'single');
  const [dateOptions, setDateOptions] = useState(null); // [{date, selectable}] across the horizon
  const [loadingDates, setLoadingDates] = useState(false);

  const dateLocked = isSign && request.status === 'published';

  // Bucket meta (reasons + amount options) for non-sign buckets
  useEffect(() => {
    if (isSign) {
      F.getConfig().then(setSignConfig).catch(e => setError(e.message));
      return;
    }
    api.getRequestTypes()
      .then(types => setBucket((types || []).find(t => t.key === type) || null))
      .catch(e => setError(e.message));
  }, [type, isSign]);

  // Sign: current work types from the request's junction summary — the detail
  // endpoint gives us names only, so match them against the config catalog.
  useEffect(() => {
    if (!isSign || !signConfig) return;
    const names = (request[F.summaryCol] || '').split(',').map(s => s.trim()).filter(Boolean);
    const ids = signConfig.work_types.filter(wt => names.includes(wt.name)).map(wt => wt.id);
    setWorkTypeIds(ids);
    setOriginalWorkTypeIds(ids);
    setWorkTypesLoaded(true);
  }, [isSign, signConfig, request[F.summaryCol]]);

  // Sign: build the eligible-date dropdown from availability across the
  // booking horizon (max 3 month fetches). The current date is always shown.
  const loadDateOptions = useCallback(async () => {
    if (!isSign || !signConfig || !request.scheduled_date || dateLocked) return;
    setLoadingDates(true);
    try {
      const today = new Date();
      today.setDate(today.getDate() + (signConfig.min_lead_days || 1));
      const firstMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
      const horizon = new Date();
      horizon.setDate(horizon.getDate() + (signConfig.horizon_days || 60));
      const lastMonth = `${horizon.getFullYear()}-${String(horizon.getMonth() + 1).padStart(2, '0')}`;

      const months = [];
      for (let ym = firstMonth; ym <= lastMonth && months.length < 4; ym = shiftMonth(ym, 1)) {
        months.push(ym);
      }
      const results = await Promise.all(months.map(month =>
        F.getAvailability({
          region: request[F.regionCol],
          estimated_minutes: Number(estimatedMinutes) || request.estimated_minutes,
          month,
        })
      ));
      // Managers get weekends too (Sep 11); days outside the region's usual
      // schedule are flagged so the label says so in the dropdown.
      const selectable = results.flatMap(r => (r.days || []).filter(d => d.selectable)
        .map(d => ({ date: d.date, offSchedule: d.service_day === false })));
      setDateOptions(selectable);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingDates(false);
    }
  }, [isSign, signConfig, request.scheduled_date, request[F.regionCol], request.estimated_minutes, estimatedMinutes, dateLocked]);

  useEffect(() => { loadDateOptions(); }, [loadDateOptions]);

  const selectedSignTypes = signConfig?.work_types?.filter(wt => workTypeIds.includes(wt.id)) || [];
  const needsSpecialDesc = selectedSignTypes.some(wt => wt.requires_description);

  function toggleWorkType(id) {
    setWorkTypeIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }

  // ── save ──
  async function handleSave(e) {
    e.preventDefault();
    setError('');
    const body = {};

    if (isSign) {
      const sameTypes = workTypeIds.length === originalWorkTypeIds.length
        && workTypeIds.every(id => originalWorkTypeIds.includes(id));
      if (workTypesLoaded && !sameTypes) {
        if (workTypeIds.length === 0) return setError('Select at least one type of work');
        body.work_type_ids = workTypeIds;
      }
      if (needsSpecialDesc && !specialSignDescription.trim()) {
        return setError(F.specialError);
      }
      if ((specialSignDescription || '') !== (request[F.specialCol] || '')) {
        body[F.specialCol] = specialSignDescription.trim() || null;
      }
      if (notes.trim() !== (request.notes || '')) {
        if (!notes.trim()) return setError('Work description is required');
        body.work_description = notes.trim();
      }
      if ((workAddress || '') !== (request.work_address || '')) body.work_address = workAddress;
      if ((accessInstructions || '') !== (request.access_instructions || '')) body.access_instructions = accessInstructions;
      if (Number(estimatedMinutes) !== request.estimated_minutes) body.estimated_minutes = Number(estimatedMinutes);
      if (request.scheduled_date && scheduledDate && scheduledDate !== request.scheduled_date) {
        body.scheduled_date = scheduledDate;
      }
      // Changing the cadence is how a standing sweep is started or stopped.
      // Only affects what happens AFTER this visit completes — occurrences that
      // already exist are separate requests.
      if (isTagging && frequency !== (request.tagging_frequency || 'single')) {
        body.frequency = frequency;
      }
    } else {
      if (property?.id && property.id !== request.property_id) body.property_id = property.id;

      if (towStyle || isItem) {
        if (reasonId && Number(reasonId) !== request.reason_id) body.reason_id = Number(reasonId);
      }
      if (towStyle) {
        if (vehicleInfo.trim() !== (request.vehicle_info || '')) {
          if (!vehicleInfo.trim()) return setError('Vehicle information is required');
          body.vehicle_info = vehicleInfo.trim();
        }
        if (notes.trim() !== (request.notes || '')) {
          if (!notes.trim()) return setError('Notes are required');
          body.notes = notes.trim();
        }
        if (bucket?.has_amount && refundCategory) {
          const amountChanged = refundCategory !== (request.refund_category || '')
            || (refundCategory === 'other' && Number(amount) !== Number(request.amount_requested));
          if (amountChanged) {
            if (refundCategory === 'other' && (!amount || Number(amount) <= 0)) {
              return setError('Enter an amount for Other');
            }
            body.refund_category = refundCategory;
            if (refundCategory === 'other') body.amount_requested = Number(amount);
          }
        }
        if (type === 'refund') {
          const payerFields = {
            payer_name: payerName, payer_phone: payerPhone, payer_email: payerEmail,
            original_payment_method: originalPaymentMethod,
            release_doc_info: releaseDocInfo, check_mailing_address: checkMailingAddress,
          };
          for (const [k, v] of Object.entries(payerFields)) {
            if ((v || '').trim() !== (request[k] || '')) body[k] = v.trim() || null;
          }
        }
      } else if (isItem) {
        if (isOtherItem) {
          if (!itemTypeOther.trim()) return setError('Describe the item you need');
          if (itemTypeOther.trim() !== (request.item_type_other || '')) body.item_type_other = itemTypeOther.trim();
        }
        if (Number(itemCount) !== Number(request.item_count)) {
          const qty = Number(itemCount);
          if (!Number.isInteger(qty) || qty <= 0) {
            return setError('Number of Items must be a whole number greater than 0');
          }
          body.item_count = qty;
        }
        if ((notes || '').trim() !== (request.notes || '')) body.notes = notes.trim();
      } else if (isEvent) {
        if (requestedDate && requestedDate !== request.requested_date) body.requested_date = requestedDate;
        if (requestorName.trim() !== (request.requestor_name || '')) {
          if (!requestorName.trim()) return setError('Requestor Name is required');
          body.requestor_name = requestorName.trim();
        }
        if (scopeOfWork.trim() !== (request.scope_of_work || '')) {
          if (!scopeOfWork.trim()) return setError('Event Details are required');
          body.scope_of_work = scopeOfWork.trim();
        }
      } else if (isVendor) {
        if (workRequestType && workRequestType !== request.work_request_type) body.work_request_type = workRequestType;
        if (requestorName.trim() !== (request.requestor_name || '')) {
          if (!requestorName.trim()) return setError('Requestor Name is required');
          body.requestor_name = requestorName.trim();
        }
        if (requestorEmail.trim() !== (request.requestor_email || '')) {
          if (!requestorEmail.trim()) return setError('Requestor Email is required');
          body.requestor_email = requestorEmail.trim();
        }
        if (scopeOfWork.trim() !== (request.scope_of_work || '')) {
          if (!scopeOfWork.trim()) return setError('Scope of Work is required');
          body.scope_of_work = scopeOfWork.trim();
        }
        if (requestedTimeline && requestedTimeline !== request.requested_timeline) {
          body.requested_timeline = requestedTimeline;
        }
      }
    }

    if (Object.keys(body).length === 0) {
      return setError('Nothing changed yet — edit a field or Cancel');
    }

    setSaving(true);
    try {
      if (isSign) await F.update(request.id, body);
      else await api.updateRequest(request.id, body);
      onSaved();
    } catch (err) {
      if (err.responseBody?.code === 'DATE_CAPACITY_CHANGED' || err.responseBody?.code === 'DATE_NOT_ELIGIBLE') {
        setError(err.responseBody.error);
        setScheduledDate(request.scheduled_date || '');
        loadDateOptions();
      } else {
        setError(err.message || 'Save failed');
      }
    } finally {
      setSaving(false);
    }
  }

  const reasons = bucket?.reasons || [];
  // Item types: keep a retired type (e.g. School Supplies) selectable on the
  // request that still carries it, so the dropdown doesn't render blank.
  const itemTypes = isItem && request.reason_id && !reasons.some(r => r.id === request.reason_id)
    ? [...reasons, { id: request.reason_id, name: request.reason_name || request.reason || 'Retired type' }]
    : reasons;
  const isOtherItem = isItem && /^other$/i.test(itemTypes.find(r => String(r.id) === String(reasonId))?.name || '');

  // Amount options for tow-style buckets that have one (Refund / Discount)
  let amountOptions = [
    { key: 'full',  label: 'Full' },
    { key: 'half',  label: 'Half' },
    { key: 'other', label: 'Other' },
  ];
  try {
    if (bucket?.amount_options) {
      const parsed = typeof bucket.amount_options === 'string'
        ? JSON.parse(bucket.amount_options) : bucket.amount_options;
      if (Array.isArray(parsed) && parsed.length > 0) amountOptions = parsed;
    }
  } catch { /* fallback */ }

  const accent = bucket?.color || '#0891b2';
  const workTypeButtons = WORK_TYPE_BUTTONS_BY_BUCKET[type] || [];

  return (
    <Modal title={`✏️ Edit ${request.type_label || type} #${request.id}`} onClose={onClose} wide>
      <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {error && <div className="alert alert-error">{error}</div>}

        {/* ───────── Sign Request ───────── */}
        {isSign && (!signConfig ? <p style={{ color: 'var(--text-muted)' }}>Loading…</p> : (
          <>
            <div>
              <label style={labelStyle}>Work Requested {req} <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(check all that apply)</span></label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
                {signConfig.work_types.map(wt => {
                  const checked = workTypeIds.includes(wt.id);
                  return (
                    <label key={wt.id} style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px',
                      border: `2px solid ${checked ? '#0891b2' : 'var(--border-subtle)'}`,
                      borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: checked ? 600 : 400,
                      background: checked ? 'rgba(8, 145, 178, 0.06)' : 'transparent',
                    }}>
                      <input type="checkbox" checked={checked} onChange={() => toggleWorkType(wt.id)} />
                      {wt.name}
                    </label>
                  );
                })}
              </div>
            </div>

            {needsSpecialDesc && (
              <div>
                <label style={labelStyle}>{F.specialLabel} {req}</label>
                <textarea className="form-input" rows={3}
                  value={specialSignDescription} onChange={e => setSpecialSignDescription(e.target.value)}
                  placeholder={F.specialPlaceholder}
                  style={{ width: '100%', padding: '10px 12px', resize: 'vertical' }} />
              </div>
            )}

            <div>
              <label style={labelStyle}>Work Description {req}</label>
              <textarea className="form-input" rows={4}
                value={notes} onChange={e => setNotes(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', resize: 'vertical' }} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
              <div>
                <label style={labelStyle}>Estimated Time {req}</label>
                <select className="form-input" style={{ width: '100%', padding: '10px 12px' }}
                  value={estimatedMinutes} onChange={e => setEstimatedMinutes(Number(e.target.value) || '')}>
                  {signConfig.duration_options.map(m => <option key={m} value={m}>{fmtDuration(m)}</option>)}
                </select>
              </div>

              {request.scheduled_date ? (
                <div>
                  <label style={labelStyle}>Work Date {req}</label>
                  {dateLocked ? (
                    <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '10px 0' }}>
                      🔒 {fmtDateLong(request.scheduled_date)} — on a published route. Remove it from the
                      route on the Schedule Board to move the date.
                    </div>
                  ) : (
                    <select className="form-input" style={{ width: '100%', padding: '10px 12px' }}
                      value={scheduledDate} onChange={e => setScheduledDate(e.target.value)}
                      disabled={loadingDates}>
                      <option value={request.scheduled_date}>
                        {fmtDateLong(request.scheduled_date)} (current)
                      </option>
                      {(dateOptions || [])
                        .filter(d => d.date !== request.scheduled_date)
                        .map(d => (
                          <option key={d.date} value={d.date}>
                            {fmtDateLong(d.date)}{d.offSchedule ? ' · off-schedule day' : ''}
                          </option>
                        ))}
                    </select>
                  )}
                  {loadingDates && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Checking availability…</div>}
                </div>
              ) : (
                <div>
                  <label style={labelStyle}>Work Date</label>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '10px 0' }}>
                    🚨 Emergency — the {F.manager} slots this on the Schedule Board.
                  </div>
                </div>
              )}
            </div>

            {isTagging && (
              <div>
                <label style={labelStyle}>Tagging Frequency</label>
                <select className="form-input" style={{ width: '100%', padding: '10px 12px' }}
                  value={frequency} onChange={e => setFrequency(e.target.value)}>
                  <option value="single">Single Request</option>
                  <option value="monthly">🔁 Monthly</option>
                  <option value="quarterly">🔁 Quarterly</option>
                </select>
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 4 }}>
                  {frequency === 'single'
                    ? 'No further visits will be created when this one is completed.'
                    : `When this visit is completed, the next one is created automatically, dated ${frequency === 'monthly' ? 'a month' : 'three months'} out.`}
                </div>
              </div>
            )}

            <div>
              <label style={labelStyle}>Work Address</label>
              <input className="form-input" style={{ width: '100%', padding: '10px 12px' }}
                value={workAddress} onChange={e => setWorkAddress(e.target.value)}
                placeholder="Blank = property address" />
            </div>

            <div>
              <label style={labelStyle}>Access Instructions</label>
              <textarea className="form-input" rows={2}
                value={accessInstructions} onChange={e => setAccessInstructions(e.target.value)}
                placeholder="Gate code, lockbox, who to contact on site…"
                style={{ width: '100%', padding: '10px 12px', resize: 'vertical' }} />
            </div>
          </>
        ))}

        {/* ───────── Property (all non-sign buckets that have one) ───────── */}
        {!isSign && request.property_id && (
          <AccountSelector
            value={property}
            onChange={setProperty}
            useOther={false}
            onToggleOther={() => {}}
            otherValue=""
            onOtherChange={() => {}}
            label="Property"
            required
          />
        )}

        {/* ───────── Tow-style (Refund / No Charge / Take Back / Discount) ───────── */}
        {towStyle && (
          <>
            <div>
              <label style={labelStyle}>Submission Reason {req}</label>
              <select value={reasonId} onChange={e => setReasonId(e.target.value)}
                className="form-input" style={{ width: '100%', padding: '10px 12px' }}>
                <option value="">Select a reason…</option>
                {reasons.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>

            <div>
              <label style={labelStyle}>Vehicle Information {req}</label>
              <input type="text" className="form-input" value={vehicleInfo}
                onChange={e => setVehicleInfo(e.target.value)}
                style={{ width: '100%', padding: '10px 12px' }} />
            </div>

            {bucket?.has_amount && (
              <div>
                <label style={labelStyle}>{bucket.label} Amount {req}</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {amountOptions.map(opt => {
                    const selected = refundCategory === opt.key;
                    return (
                      <button key={opt.key} type="button" onClick={() => setRefundCategory(opt.key)}
                        style={{
                          padding: '10px 24px', fontSize: 14, fontWeight: 700, borderRadius: 8,
                          border: `2px solid ${selected ? accent : 'var(--border-subtle)'}`,
                          background: selected ? `${accent}15` : '#fff',
                          color: selected ? accent : 'var(--text-muted)', cursor: 'pointer',
                        }}>{opt.label}</button>
                    );
                  })}
                </div>
                {refundCategory === 'other' && (
                  <input type="number" step="0.01" min="0" className="form-input"
                    value={amount} onChange={e => setAmount(e.target.value)}
                    placeholder="e.g. 75.00"
                    style={{ width: 200, padding: '10px 12px', marginTop: 10 }} />
                )}
              </div>
            )}

            <div>
              <label style={labelStyle}>Notes {req}</label>
              <textarea className="form-input" rows={4} value={notes}
                onChange={e => setNotes(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', resize: 'vertical' }} />
            </div>

            {type === 'refund' && (
              <div style={{
                padding: 14, border: '1px solid var(--border-subtle)', borderRadius: 10,
                background: '#fafafa', display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10,
              }}>
                <div style={{ gridColumn: '1 / -1', fontWeight: 700, fontSize: 14 }}>💳 Payer Information</div>
                <div>
                  <label style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, display: 'block', marginBottom: 4 }}>Payer Name</label>
                  <input type="text" className="form-input" value={payerName}
                    onChange={e => setPayerName(e.target.value)} style={{ width: '100%', padding: '8px 10px' }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, display: 'block', marginBottom: 4 }}>Payer Phone</label>
                  <input type="tel" className="form-input" value={payerPhone}
                    onChange={e => setPayerPhone(e.target.value)} style={{ width: '100%', padding: '8px 10px' }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, display: 'block', marginBottom: 4 }}>Payer Email</label>
                  <input type="email" className="form-input" value={payerEmail}
                    onChange={e => setPayerEmail(e.target.value)} style={{ width: '100%', padding: '8px 10px' }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, display: 'block', marginBottom: 4 }}>Original Payment Method</label>
                  <select className="form-input" value={originalPaymentMethod}
                    onChange={e => setOriginalPaymentMethod(e.target.value)} style={{ width: '100%', padding: '8px 10px' }}>
                    <option value="">Select…</option>
                    <option value="credit_card">Credit Card</option>
                    <option value="debit_card">Debit Card</option>
                    <option value="cash">Cash</option>
                    <option value="check">Check</option>
                    <option value="unknown">Unknown</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, display: 'block', marginBottom: 4 }}>Release Doc / Receipt #</label>
                  <input type="text" className="form-input" value={releaseDocInfo}
                    onChange={e => setReleaseDocInfo(e.target.value)} style={{ width: '100%', padding: '8px 10px' }} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, display: 'block', marginBottom: 4 }}>Check Mailing Address</label>
                  <input type="text" className="form-input" value={checkMailingAddress}
                    onChange={e => setCheckMailingAddress(e.target.value)} style={{ width: '100%', padding: '8px 10px' }} />
                </div>
              </div>
            )}
          </>
        )}

        {/* ───────── Item Request ───────── */}
        {isItem && (
          <>
            <div>
              <label style={labelStyle}>Item Type {req}</label>
              <select value={reasonId} onChange={e => setReasonId(e.target.value)}
                className="form-input" style={{ width: '100%', padding: '10px 12px' }}>
                <option value="">Select an item type…</option>
                {itemTypes.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
            {isOtherItem && (
              <div>
                <label style={labelStyle}>What item do you need? {req}</label>
                <input type="text" className="form-input" value={itemTypeOther}
                  onChange={e => setItemTypeOther(e.target.value)}
                  placeholder="e.g. Pool party snacks"
                  style={{ width: '100%', padding: '10px 12px' }} />
              </div>
            )}
            <div>
              <label style={labelStyle}>Number of Items {req}</label>
              <input type="number" min="1" step="1" className="form-input"
                value={itemCount} onChange={e => setItemCount(e.target.value)}
                style={{ width: 200, padding: '10px 12px' }} />
            </div>
            <div>
              <label style={labelStyle}>Notes</label>
              <textarea className="form-input" rows={3} value={notes}
                onChange={e => setNotes(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', resize: 'vertical' }} />
            </div>
          </>
        )}

        {/* ───────── Sponsored Event ───────── */}
        {isEvent && (
          <>
            <div>
              <label style={labelStyle}>Requested Date {req}</label>
              <input type="date" className="form-input" value={requestedDate}
                onChange={e => setRequestedDate(e.target.value)} style={{ padding: '10px 12px' }} />
            </div>
            <div>
              <label style={labelStyle}>Requestor Name {req}</label>
              <input type="text" className="form-input" value={requestorName}
                onChange={e => setRequestorName(e.target.value)}
                style={{ width: '100%', padding: '10px 12px' }} />
            </div>
            <div>
              <label style={labelStyle}>Event Details {req}</label>
              <textarea className="form-input" rows={5} value={scopeOfWork}
                onChange={e => setScopeOfWork(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', resize: 'vertical' }} />
            </div>
          </>
        )}

        {/* ───────── Vendor buckets (Striping Bid / Valet Trash) ───────── */}
        {isVendor && (
          <>
            <div>
              <label style={labelStyle}>Work Request Type {req}</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {workTypeButtons.map(opt => {
                  const selected = workRequestType === opt.key;
                  return (
                    <button key={opt.key} type="button" onClick={() => setWorkRequestType(opt.key)}
                      style={{
                        padding: '10px 24px', fontSize: 14, fontWeight: 700, borderRadius: 8,
                        border: `2px solid ${selected ? accent : 'var(--border-subtle)'}`,
                        background: selected ? `${accent}15` : '#fff',
                        color: selected ? accent : 'var(--text-muted)', cursor: 'pointer',
                      }}>{opt.label}</button>
                  );
                })}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
              <div>
                <label style={labelStyle}>Requestor Name {req}</label>
                <input type="text" className="form-input" value={requestorName}
                  onChange={e => setRequestorName(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px' }} />
              </div>
              <div>
                <label style={labelStyle}>Requestor Email {req}</label>
                <input type="email" className="form-input" value={requestorEmail}
                  onChange={e => setRequestorEmail(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px' }} />
              </div>
            </div>
            <div>
              <label style={labelStyle}>Details about Scope of Work {req}</label>
              <textarea className="form-input" rows={5} value={scopeOfWork}
                onChange={e => setScopeOfWork(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', resize: 'vertical' }} />
            </div>
            <div>
              <label style={labelStyle}>Requested Timeline {req}</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {[
                  { key: 'emergency', label: '🚨 Emergency', fg: '#ef4444' },
                  { key: 'standard',  label: 'Standard',     fg: accent },
                ].map(opt => {
                  const selected = requestedTimeline === opt.key;
                  return (
                    <button key={opt.key} type="button" onClick={() => setRequestedTimeline(opt.key)}
                      style={{
                        padding: '10px 28px', fontSize: 14, fontWeight: 700, borderRadius: 8,
                        border: `2px solid ${selected ? opt.fg : 'var(--border-subtle)'}`,
                        background: selected ? `${opt.fg}15` : '#fff',
                        color: selected ? opt.fg : 'var(--text-muted)', cursor: 'pointer',
                      }}>{opt.label}</button>
                  );
                })}
              </div>
            </div>
          </>
        )}

        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          Photos can't be changed here — they stay as submitted.
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 4 }}>
          <button type="button" className="btn btn-muted" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="btn btn-gold" disabled={saving} style={{ fontWeight: 700 }}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
